import { useState } from 'react';
import { DororaidIcon } from '../../../shared/ui/DororaidIcon.jsx';
import { RaidPracticeDeckDisplay } from './RaidPracticeDeckPicker.jsx';
import { RaidPracticeSquadDetailModal } from './RaidPracticeSquadDetailModal.jsx';
import { RAID_PRACTICE_ACTION_LABELS } from './raidPracticeActionLabels.js';

function formatEok(value) {
  if (value === null || value === undefined) return '-';
  return `${Number(value || 0).toLocaleString('ko-KR', { maximumFractionDigits: 3 })}억`;
}

// A real attack only takes over a practice slot when the two decks are identical, so on those cards
// the practice record is a like-for-like measurement of the same team and the gap is worth reading.
// It rides on the damage line rather than a line of its own: an extra line would make the cards that
// have a gap taller than the ones beside them, and these sit in grids of five bosses.
function formatDeltaEok(value) {
  const number = Number(value || 0);
  const sign = number > 0 ? '+' : number < 0 ? '-' : '';
  return `${sign}${Math.abs(number).toLocaleString('ko-KR', { maximumFractionDigits: 3 })}억`;
}

function deltaClassName(value) {
  const number = Number(value || 0);
  if (number > 0) return 'positive';
  if (number < 0) return 'negative';
  return '';
}

function statusLabel(entry) {
  if (entry?.source === 'real_applied') return '실전 반영';
  if (entry?.source === 'real_only') return '실전 기록';
  if (entry?.manual?.used) return '공격완료';
  return '';
}

function displaySlotTitle(rawTitle, slotNo, actual, readOnly) {
  if (actual && readOnly) return '실전 기록 덱';
  if (actual && !rawTitle) return '실전 기록 덱';

  const title = String(rawTitle || '').trim();
  const defaultPracticeTitle = title.match(/^모의전\s*슬롯\s*(\d+)$/i);
  if (!title || defaultPracticeTitle) return `슬롯 ${defaultPracticeTitle?.[1] || slotNo}`;
  return title;
}

function memberOpenidFromEntry(manual, actual) {
  return String(
    manual?.memberOpenid
      || manual?.member_openid
      || actual?.memberOpenid
      || actual?.member_openid
      || '',
  ).trim();
}

export function RaidPracticeSlotCard({
  entry,
  mobile = false,
  readOnly = false,
  canEdit = false,
  viewerOpenid = '',
  onEdit,
  onDelete,
  working = '',
}) {
  const [detailOpen, setDetailOpen] = useState(false);
  const manual = entry?.manual || null;
  const actual = entry?.actual || null;
  const slotNo = Number(manual?.slotNo || entry?.slotNo || actual?.attackNo || 1);
  const title = displaySlotTitle(manual?.title, slotNo, actual, readOnly);
  const damage = entry?.displayDamageEok ?? actual?.damageEok ?? manual?.damageEok ?? 0;
  const deleteWorking = working === `delete:${slotNo}`;
  const className = mobile ? 'mobile-practice-slot-card-v2' : 'desktop-practice-slot-card-v2';
  const sourceClass = entry?.source ? `source-${entry.source}` : '';
  const slotLabel = `SLOT ${slotNo}`;
  const status = statusLabel(entry);
  const realApplied = entry?.source === 'real_applied';
  const showDelta = realApplied
    && Number.isFinite(Number(entry?.plannedDamageEok))
    && Number.isFinite(Number(entry?.deltaEok));
  const showActions = !realApplied && (!readOnly || canEdit);
  const editLabel = manual ? RAID_PRACTICE_ACTION_LABELS.edit : RAID_PRACTICE_ACTION_LABELS.add;
  const deleteLabel = deleteWorking ? '삭제 중' : RAID_PRACTICE_ACTION_LABELS.remove;
  const deck = entry?.displayDeck || manual?.deck || actual?.deck || [];
  // A record of your own resolves from your own synced NIKKEs. get_union_member_detail is
  // leader/deputy only and rejects even a self lookup, and an actual record carries your own openid,
  // so routing your own row through it turned 상세보기 into a permission error.
  const entryMemberOpenid = memberOpenidFromEntry(manual, actual);
  const viewerMemberOpenid = String(viewerOpenid || '').trim();
  const ownEntry = !readOnly || (Boolean(viewerMemberOpenid) && entryMemberOpenid === viewerMemberOpenid);
  const detailMemberOpenid = ownEntry ? '' : entryMemberOpenid;

  return (
    <article className={`${className} ${sourceClass} ${actual ? 'has-actual' : ''} ${manual?.used ? 'used' : ''} ${readOnly ? 'read-only' : ''}`}>
      <div className={`${className}-head`}>
        <div><span>{slotLabel}</span><strong>{title}</strong></div>
        <span className="raid-practice-slot-head-actions" role="group" aria-label="기록 상태와 동작">
          {status ? <em>{status}</em> : null}
          <button
            type="button"
            className="raid-practice-slot-detail-button"
            aria-label={RAID_PRACTICE_ACTION_LABELS.detail}
            title={RAID_PRACTICE_ACTION_LABELS.detail}
            onClick={() => setDetailOpen(true)}
          >
            <DororaidIcon name="details" size={mobile ? 18 : 16} />
          </button>
          {showActions ? (
            <>
              <button
                type="button"
                className={`raid-practice-slot-icon-button ${manual ? 'edit' : 'add'}`}
                aria-label={editLabel}
                title={editLabel}
                disabled={!onEdit || Boolean(working)}
                onClick={() => onEdit?.(entry)}
              >
                <DororaidIcon name={manual ? 'edit' : 'plus'} size={mobile ? 18 : 16} />
              </button>
              {manual?.id && onDelete ? (
                <button
                  type="button"
                  className="raid-practice-slot-icon-button danger"
                  aria-label={deleteLabel}
                  title={deleteLabel}
                  disabled={Boolean(working)}
                  onClick={() => onDelete(manual.id, slotNo)}
                >
                  <DororaidIcon name="close" size={mobile ? 18 : 16} />
                </button>
              ) : null}
            </>
          ) : null}
        </span>
      </div>

      <div className={`${className}-damage`}>
        <div title={showDelta ? `같은 덱으로 잰 모의전 ${formatEok(entry.plannedDamageEok)} 대비 ${formatDeltaEok(entry.deltaEok)}` : undefined}>
          <span>{realApplied ? '실전딜' : '딜량'}</span>
          <strong>
            {formatEok(damage)}
            {showDelta ? (
              <em className={`raid-practice-slot-delta ${deltaClassName(entry.deltaEok)}`}>{formatDeltaEok(entry.deltaEok)}</em>
            ) : null}
          </strong>
        </div>
      </div>

      <RaidPracticeDeckDisplay deck={deck} mobile={mobile} />

      {detailOpen ? (
        <RaidPracticeSquadDetailModal
          deck={deck}
          personalEnabled={ownEntry || Boolean(detailMemberOpenid)}
          memberOpenid={detailMemberOpenid}
          onClose={() => setDetailOpen(false)}
        />
      ) : null}
    </article>
  );
}
